/** 화면 안내는 문서의 축약본이 아니다. 조작 위치에 맞는 짧은 설명만 둔다. */
export interface ScreenHint {
  id: string;
  /** 기존 접근성·구조 셀렉터를 안정적으로 쓸 수 있는 화면의 선택적 고정점. */
  selector?: string;
  title: string;
  short: string;
  detail: string;
  /** 순서나 제한처럼 특히 놓치기 쉬운 안내만 같은 팔레트의 진한 테두리로 표시한다. */
  attention?: boolean;
}

export interface ScreenHintSet {
  docId: string;
  screens: string[];
  hints: ScreenHint[];
  /** 고정점이 머리 띠 안에 있는 화면(작성 화면). 띠 아래로 숨지 않으니 거르지 않고, 메모지는 고정점 바로 아래에 둔다. */
  headerAnchors?: boolean;
}

const recruitHints: ScreenHintSet = {
  docId: 'programs-recruit',
  screens: ['/programs/[id]/recruit'],
  hints: [
    {
      id: 'forms',
      title: '지원 서식',
      short: '카드를 누르면 아래 명단에 그 서식의 지원만 보입니다.',
      detail: '같은 카드를 다시 누르면 전체 명단으로 돌아옵니다. 숫자는 서식별 지원·제출·반려·선발 현황입니다.',
    },
    {
      id: 'filters',
      title: '명단 찾기',
      short: '이름이나 서식으로 찾고, 상태와 팀으로 좁혀 보세요.',
      detail: '초기화를 누르면 검색어와 필터가 모두 풀립니다. 지원서에서 돌아와도 고른 조건은 유지됩니다.',
    },
    {
      id: 'actions',
      title: '선발과 팀 편성',
      short: '팀을 쓰는 경우, 참여자 등록이 팀 편성보다 먼저입니다.',
      detail: '명단에서 사람을 고른 뒤 참여자로 등록하세요. 등록한 사람만 팀에 넣을 수 있습니다.',
      attention: true,
    },
  ],
};

const dashboardHints: ScreenHintSet = {
  docId: 'dashboard',
  screens: ['/dashboard'],
  hints: [
    { id: 'dashboard-programs', title: '내 프로그램', short: '프로그램을 열어 내 서류와 참여 현황을 확인하세요.', detail: '담당자에게는 운영 중인 프로그램이, 참여자에게는 참여 중인 프로그램이 보입니다.' },
  ],
};

const documentsHints: ScreenHintSet = {
  docId: 'documents',
  screens: ['/documents'],
  hints: [
    { id: 'documents-status', title: '상태로 거르기', short: '상태 단추를 누르면 그 상태의 문서가 빠지고, 다시 누르면 돌아옵니다.', detail: '단추의 말과 색은 문서 줄의 배지와 같고, 옆 숫자는 그 상태의 문서 수입니다. 배지를 누르면 지금 상태 설명이 뜹니다. 반려된 문서에는 사유가 함께 표시됩니다.' },
    { id: 'documents-list', selector: 'main table', title: '문서 열기', short: '줄을 눌러 이어 쓰거나 제출본 PDF를 확인하세요.', detail: '새 문서는 홈의 프로그램 카드나 프로그램 화면 「작성 가능한 서류」에서 시작합니다.' },
    { id: 'documents-team', title: '팀원 문서', short: '같은 팀이 낸 문서를 제출본 PDF로 봅니다.', detail: '팀 문서는 처음에는 작성자·팀장·담당자만 봅니다. 팀장이 줄마다 「팀원 공개」를 켜면 같은 팀원도 봅니다.' },
  ],
};

const programsHints: ScreenHintSet = {
  docId: 'programs',
  screens: ['/programs'],
  hints: [
    { id: 'programs-search', title: '프로그램 찾기', short: '이름으로 찾고 프로그램을 열어보세요.', detail: '참여자는 내 서류를, 담당자는 지원·제출·운영 화면을 볼 수 있습니다.' },
    { id: 'programs-create', title: '프로그램 만들기', short: '새 업무는 프로그램부터 만듭니다.', detail: '이름과 유형을 정하세요. 만든 사람은 그 프로그램의 관리자가 됩니다.' },
  ],
};

const templatesHints: ScreenHintSet = {
  docId: 'templates',
  screens: ['/templates'],
  hints: [
    { id: 'templates-upload', title: '서식 등록', short: '새 서식을 올리거나 통합본을 나눌 수 있습니다.', detail: '등록한 서식은 프로그램 「제출/서식 관리」의 「서식 추가」 › 「기존 서식 가져오기」로 사본을 가져옵니다.' },
    { id: 'templates-search', title: '서식 찾기', short: '이름으로 찾고 소속·종류·상태로 좁혀 보세요.', detail: '모집 지원에 쓸 서식은 프로그램의 모집 정책에서 따로 골라야 합니다.' },
  ],
};

const prefillHints: ScreenHintSet = {
  docId: 'prefill',
  screens: ['/prefill'],
  hints: [
    { id: 'prefill-tabs', title: '내 참고자료', short: '참고정보·참고문서·즐겨찾기 이미지를 탭에서 관리합니다.', detail: '참고정보는 AI 초안이 읽고, 참고문서는 작성 화면 「참고자료」에서 골라 더합니다. 문서에 자동으로 들어가지는 않습니다.' },
    { id: 'prefill-content', selector: 'main [role="tabpanel"]:not([hidden])', title: '내용 확인', short: '참고정보는 고친 뒤 「저장」을 눌러 주세요. 파일과 이미지는 올리면 바로 보관됩니다.', detail: '참고자료가 실제 문서에 맞게 쓰였는지는 제출 전에 확인해야 합니다.' },
  ],
};

const timetableHints: ScreenHintSet = {
  docId: 'timetable',
  screens: ['/timetable'],
  hints: [
    { id: 'timetable-term', title: '학기 선택', short: '일정을 볼 학기를 고르세요. 처음에는 오늘이 든 학기입니다.', detail: '수업과 근무 일정은 선택한 학기를 기준으로 보입니다.' },
    { id: 'timetable-recognition', title: '수업시간표 인식', short: '시간표 파일을 올리고 인식 결과를 확인한 뒤 적용하세요.', detail: '이미지나 PDF를 올릴 수 있습니다. 수업을 직접 등록할 수도 있습니다.' },
    { id: 'timetable-work', title: '근무 일정', short: '근무 시간을 등록하세요. 다른 일정과 겹쳐도 등록할 수 있습니다.', detail: '근무 일정은 근무상황부를 작성할 때 바탕이 됩니다.' },
  ],
};

const settingsHints: ScreenHintSet = {
  docId: 'settings',
  screens: ['/settings/account'],
  hints: [
    { id: 'settings-profile', title: '계정 정보', short: '이름과 추가 정보를 확인하고 변경하세요.', detail: '바꾼 내용은 「저장하기」를 눌러야 반영됩니다.' },
    { id: 'settings-consents', selector: '#consents', title: '선택 동의', short: 'AI 작성 지원 같은 선택 동의를 여기서 하거나 철회합니다.', detail: '제목을 누르면 본문이 열립니다. 「동의 철회」 뒤에도 「동의하기」로 다시 동의할 수 있습니다.' },
    { id: 'settings-password', title: '비밀번호', short: '「비밀번호 변경」을 누르면 로그인 화면에서 바꿉니다.', detail: '회원탈퇴는 되돌릴 수 없습니다. 작성한 문서와 프로그램에 낸 기록은 이 단추로 지워지지 않습니다. 프로그램에 낸 기록이 있으면 탈퇴가 바로 되지 않을 수 있습니다. 그때는 담당자에게 문의해 주세요.' },
  ],
};

const settingsAboutHints: ScreenHintSet = {
  docId: 'settings',
  screens: ['/settings/about'],
  hints: [
    { id: 'settings-about-service', title: '서비스 소개', short: '한채움AI의 용도와 구성 정보를 볼 수 있습니다.', detail: '아래에 만든 팀과 오픈소스 사용 내역이 있습니다.' },
  ],
};

const settingsLicensesHints: ScreenHintSet = {
  docId: 'settings',
  screens: ['/settings/licenses'],
  hints: [
    { id: 'settings-licenses-list', title: '오픈소스 사용 내역', short: '소프트웨어별 라이선스를 확인하세요.', detail: '각 항목의 링크에서 라이선스 원문을 볼 수 있습니다.' },
  ],
};

const adminUsersHints: ScreenHintSet = {
  docId: 'admin-users',
  screens: ['/admin/users'],
  hints: [
    { id: 'admin-users-search', selector: 'main input[placeholder="이메일 또는 닉네임 검색..."]', title: '사용자 찾기', short: '이메일이나 닉네임으로 찾고 역할로 좁혀 보세요.', detail: '연필 단추로 이름·닉네임·전화번호·신분·학번(교원은 사번)을 고칠 수 있습니다.' },
    { id: 'admin-users-list', title: '역할 변경', short: '역할을 바꾼 뒤에는 사용자가 다시 로그인해야 적용됩니다.', detail: '내 역할보다 낮은 계정만 바꾸거나 지울 수 있습니다. 삭제는 로그인 계정까지 지우며 되돌릴 수 없습니다.', attention: true },
  ],
};

const adminIssuesHints: ScreenHintSet = {
  docId: 'admin-issues',
  screens: ['/admin/issues'],
  hints: [
    { id: 'admin-issues-search', selector: 'main input[placeholder="설명 또는 이메일 검색..."]', title: '신고 찾기', short: '설명이나 신고자 이름·이메일로 찾고 상태로 좁혀 보세요.', detail: '줄을 누르면 설명과 스크린샷, 페이지 주소, 진단 정보를 볼 수 있습니다.' },
    { id: 'admin-issues-list', title: '답변과 처리', short: '신고를 열어 코멘트로 답하고 상태를 바꾼 뒤 저장합니다.', detail: '코멘트를 남기면 신고자에게 메일이 갑니다. 신고자는 내 신고 화면에서도 확인합니다.' },
  ],
};

const adminSystemHints: ScreenHintSet = {
  docId: 'admin-system',
  screens: ['/admin/system'],
  hints: [
    { id: 'admin-system-tabs', title: '설정 갈래', short: '일반·프롬프트·LLM / API·주기 작업을 탭에서 고르세요.', detail: '서비스 정보, 프롬프트 버전, 기능별 모델 배정, 정기 작업의 실행 이력을 각 탭에서 다룹니다.' },
    { id: 'admin-system-content', selector: 'main [role="tabpanel"]:not([hidden])', title: '설정 내용', short: '프롬프트는 「저장」, 모델 배정은 「일괄 저장」을 눌러야 반영됩니다.', detail: '주기 작업 켜기·끄기는 바로 반영됩니다. 실패한 작업은 이름을 눌러 실행 이력의 메시지를 보세요.' },
  ],
};

const adminUsageHints: ScreenHintSet = {
  docId: 'admin-system',
  screens: ['/admin/usage'],
  hints: [
    { id: 'admin-usage-period', title: '기간', short: '위 목록에서 기간을 고르세요.', detail: '오른쪽에 알림 기준(한 시간·하루 횟수)이 적혀 있습니다.' },
    { id: 'admin-usage-list', title: '사람별 사용량', short: '기준을 넘은 횟수는 강조됩니다.', detail: '시도는 성공과 실패를 합친 수입니다. 비회원은 받은 메일 주소로 묶어 셉니다. 막지는 않고 알림만 보냅니다.' },
  ],
};

const adminConsentListHints: ScreenHintSet = {
  docId: 'admin-system',
  screens: ['/admin/system/consent'],
  hints: [
    { id: 'admin-consent-list', title: '동의 정책', short: '정책을 만들고 삭제된 정책을 함께 볼 수 있습니다.', detail: '정책 상세에서 현재 버전과 이전 본문을 확인하세요.' },
  ],
};

const adminConsentDetailHints: ScreenHintSet = {
  docId: 'admin-system',
  screens: ['/admin/system/consent/[policyId]'],
  hints: [
    { id: 'admin-consent-versions', title: '버전 이력', short: '발행된 본문과 현재 버전을 확인하세요.', detail: '새 버전은 별도 주소에서 작성·미리보기 후 발행합니다.' },
  ],
};

const adminConsentEditorHints: ScreenHintSet = {
  docId: 'admin-system',
  screens: ['/admin/system/consent/[policyId]/new-version'],
  hints: [
    { id: 'admin-consent-editor', title: '작성과 미리보기', short: '본문을 쓰고 미리보기에서 읽어 보세요.', detail: 'Markdown 표와 링크도 사용할 수 있습니다.' },
    { id: 'admin-consent-publish', title: '새 버전 발행', short: '발행 전에 전체 내용을 확인하세요.', detail: '발행하면 현재 본문이 바뀝니다. 필수 정책이면 모든 사용자가 다시 동의해야 합니다.', attention: true },
  ],
};

const programDetailHints: ScreenHintSet = {
  docId: 'programs',
  screens: ['/programs/[id]'],
  hints: [
    { id: 'program-top-tabs', title: '프로그램 화면', short: '제출/서식 관리·결재 준비·예산 관리·지원 확인·프로그램관리를 여기서 고릅니다.', detail: '내 권한과 프로그램 설정에 따라 보이는 탭이 다릅니다.' },
  ],
};

const submissionHints: ScreenHintSet = {
  docId: 'programs-submissions',
  screens: ['/programs/[id]/submissions', '/programs/[id]/fill-docs'],
  hints: [
    { id: 'program-top-tabs', title: '제출/서식 관리', short: '참여자의 제출과 작성 상태를 확인합니다.', detail: '서식별 제출 목록에서 문서를 열어 검토하세요.' },
    { id: 'submission-content', title: '서식 찾기', short: '서식 이름으로 찾고, 「마감 포함」을 켜면 마감된 서식도 봅니다.', detail: '서식을 열면 제출 문서와 처리 상태를 확인할 수 있습니다.' },
  ],
};

const manageMenuHint: ScreenHint = { id: 'program-manage-tabs', selector: '[data-help-anchor="program-manage-tabs"], [data-help-anchor="program-top-tabs"]', title: '운영 메뉴', short: '제출/서식 관리·결재 준비·예산 관리는 상단에서, 참여자·팀·정책·담당자·사용량은 프로그램관리에서 엽니다.', detail: '항목을 바꾸면 주소도 함께 바뀌어 현재 화면을 공유할 수 있습니다.' };

const manageHints: ScreenHintSet = {
  docId: 'programs-manage',
  screens: ['/programs/[id]/manage', '/programs/[id]/manage/participants', '/programs/[id]/manage/policies', '/programs/[id]/manage/templates', '/programs/[id]/manage/members'],
  hints: [manageMenuHint],
};

const budgetHints: ScreenHintSet = {
  docId: 'programs-manage',
  screens: ['/programs/[id]/manage/budget'],
  hints: [
    manageMenuHint,
    { id: 'program-budget-edit', selector: 'main section[aria-label="팀·개인별 예산"]', title: '예산 고치기', short: '팀 줄의 「수정」으로 배정액·신청금액·사용금액·잔액을 고칩니다.', detail: '고친 칸은 점선 밑줄이 생기고, 마우스를 올리면 서류로 셈한 값이 보입니다. 비우고 저장하면 서류 셈으로 돌아갑니다. 학생 「내 예산」도 같은 숫자를 봅니다.' },
  ],
};

const intakeHints: ScreenHintSet = {
  docId: 'programs-intake',
  screens: ['/programs/[id]/manage/intake'],
  hints: [
    { id: 'program-top-tabs', title: '지원서 취합', short: '밖에서 받은 지원서 파일을 한채움 지원서로 넣습니다.', detail: '등록된 추출 설정이 있는 프로그램에서만 이 탭이 열립니다.' },
  ],
};

const intakeDetailHints: ScreenHintSet = {
  docId: 'programs-intake',
  screens: ['/programs/[id]/manage/intake/[itemId]'],
  hints: [
    { id: 'intake-detail-title', title: '지원서 대조', short: '왼쪽 원본 PDF 와 오른쪽 값 목록을 대조하세요.', detail: '이 주소를 동료에게 보낼 수 있습니다. 「목록으로」를 누르면 표로 돌아갑니다.' },
    { id: 'intake-detail-fields', title: '값 목록', short: '틀린 값을 고치고 「저장」을 누르세요.', detail: '다 맞으면 위의 「지원서로 넣기」로 이 건을 지원서로 만듭니다. 넣은 뒤에는 값을 고칠 수 없습니다.', attention: true },
  ],
};

const excelHints: ScreenHintSet = {
  docId: 'programs-excel',
  screens: ['/programs/[id]/manage/excel', '/intake'],
  hints: [
    { id: 'excel-list', title: '결재 준비', short: '결재에 올릴 엑셀과 제출본 PDF 묶음을 차수별로 만듭니다.', detail: '카드마다 올릴 건 수와 가장 최근 차수의 상태가 보입니다.' },
  ],
};

const teamsHints: ScreenHintSet = {
  docId: 'teams',
  screens: ['/programs/[id]/manage/teams', '/programs/[id]/recruit/teams'],
  hints: [
    { id: 'program-manage-tabs', selector: '[data-help-anchor="program-manage-tabs"], main h1', title: '팀 편성', short: '참여자로 등록한 사람을 팀에 넣거나 뺍니다.', detail: '팀에서 빼도 그 사람이 제출한 문서는 남습니다. 팀 해제나 자동 짝짓기는 하지 않습니다.', attention: true },
  ],
};

const myProgramHints: ScreenHintSet = {
  docId: 'programs-my',
  screens: ['/programs/[id]/my'],
  hints: [
    { id: 'my-program-home', selector: 'main h1, main h2', title: '내 프로그램', short: '참여 중인 프로그램의 문서와 제출 상태를 봅니다.', detail: '「작성 가능한 서류」에서 새 문서를 열고, 「제출한 문서」에서 상태를 확인하세요.' },
  ],
};

const myProgramBudgetHints: ScreenHintSet = {
  docId: 'programs-my',
  screens: ['/programs/[id]/my/budget'],
  hints: [
    { id: 'my-program-budget', title: '내 예산', short: '배정액·신청금액·사용금액·잔액을 확인하세요.', detail: '건별 내역은 아래 「신청·사용 내역」에 있습니다. 금액이 맞지 않으면 프로그램 담당자에게 확인하세요.' },
  ],
};

const myIssuesHints: ScreenHintSet = {
  docId: 'my-issues',
  screens: ['/my-issues'],
  hints: [
    { id: 'my-issues-list', title: '내 신고', short: '보낸 신고와 처리 상태를 확인합니다.', detail: '신고를 열면 관리자 답변과 이전 대화를 볼 수 있습니다.' },
  ],
};

const startHints: ScreenHintSet = {
  docId: 'start',
  screens: ['/'],
  hints: [
    { id: 'landing-programs', title: '모집 중인 프로그램', short: '모집 중인 프로그램에서 지원 서식을 고르세요.', detail: '한 프로그램에 지원 서식이 여러 개일 수 있습니다. 역할에 맞는 서식을 선택하세요.' },
  ],
};

const consentHints: ScreenHintSet = {
  docId: 'start',
  screens: ['/consent'],
  hints: [
    { id: 'consent-policies', title: '필요한 동의', short: '내용을 읽고 필요한 항목을 확인하세요.', detail: '필수 동의를 마치면 원래 열려던 화면으로 돌아갑니다. 선택 동의는 계정 정보의 「선택 동의」에서 바꿉니다.' },
  ],
};

const guestApplyHints: ScreenHintSet = {
  docId: 'guest-apply',
  screens: ['/apply-start/[publicLinkId]'],
  hints: [
    { id: 'apply-identity', title: '지원 정보', short: '이름·학번·이메일·전화번호를 입력하세요.', detail: '이메일로 계정과 작성 주소 안내가 갑니다. 이메일 외에는 정해진 형식을 강요하지 않습니다.' },
  ],
};

const guestUploadHints: ScreenHintSet = {
  docId: 'guest-apply',
  screens: ['/guest-upload/[accessId]'],
  hints: [
    { id: 'guest-upload-submit', title: '서명·사진 제출', short: 'PDF에서 위치를 확인한 뒤 제출하세요.', detail: '서명이나 사진을 놓은 뒤 제출 단추가 켜집니다. 제출한 내용은 누적되며 이미 보낸 항목은 다시 보내지 않습니다.' },
  ],
};

const editorHints: ScreenHintSet = {
  docId: 'editor',
  screens: ['/ai-editor/[accessId]', '/guest-apply/[accessId]'],
  headerAnchors: true,
  hints: [
    { id: 'editor-views', title: '작성과 PDF', short: '본문을 쓰고 PDF에서 서명·그림을 확인하세요.', detail: '두 화면을 오가며 확인할 수 있습니다. 서명 요청을 보낸 뒤에는 ①이 잠기고, ②의 「재편집」으로 풀면 받은 서명이 모두 지워집니다.' },
    { id: 'editor-submit', title: '최종 제출', short: '제출본 PDF를 확인한 뒤 제출하세요.', detail: '처음 누르면 제출본 PDF가 열리고, 다시 누르면 확인 창에서 냅니다. 담당자가 열기 전에는 「제출 회수」로 되돌릴 수 있습니다.', attention: true },
  ],
};

const fillDocListHints: ScreenHintSet = {
  docId: 'programs-submissions',
  screens: ['/programs/[id]/fill-docs/[requestId]'],
  hints: [
    { id: 'fill-doc-link', title: '참여 링크', short: '이 서식의 참여 주소를 복사할 수 있습니다.', detail: '「접수 중단」하면 새 참여는 막히지만 이미 제출한 문서와 PDF는 남습니다.' },
    { id: 'fill-doc-filter', title: '제출 찾기', short: '제목·이름·이메일로 찾고 상태 단추로 좁히세요.', detail: '같은 사람이 여러 번 제출할 수 있습니다. 제출일시로 구분하세요.' },
  ],
};

const submissionDetailHints: ScreenHintSet = {
  docId: 'programs-submissions',
  screens: ['/programs/[id]/fill-docs/[requestId]/items/[itemId]'],
  hints: [
    { id: 'submission-detail-title', title: '제출 상세', short: '이 제출의 제목과 처리 상태를 확인하세요.', detail: '처음 열면 작성자는 이 제출을 회수할 수 없습니다. 목록으로 돌아가도 검색어가 유지되고, 화면 주소를 동료에게 보낼 수 있습니다.' },
    { id: 'submission-detail-actions', title: '검토 결과', short: '확인 뒤 검토 완료하거나 사유를 적어 반려하세요.', detail: '신청 금액 서식은 검토 완료로 결재에 올리고, 내부결재가 끝나면 「승인 메일 발송」을 누릅니다. 반려된 참여자는 같은 작성 주소에서 고쳐 다시 제출합니다. 반려를 거두려면 그 자리의 「반려 취소」를 누릅니다(학생에게 메일이 갑니다).', attention: true },
  ],
};

const recruitDetailHints: ScreenHintSet = {
  docId: 'programs-recruit',
  screens: ['/programs/[id]/recruit/[rowKey]'],
  hints: [
    { id: 'recruit-detail-title', title: '지원 확인', short: '지원 역할과 서식, 현재 상태를 확인하세요.', detail: '이 주소를 동료에게 보낼 수 있습니다. 명단으로 돌아가도 검색과 필터가 유지됩니다.' },
    { id: 'recruit-detail-actions', title: '선발과 팀', short: '참여자로 등록한 뒤 팀에 넣을 수 있습니다.', detail: '등록하지 않은 사람은 미선발입니다. 팀에서 빼도 이미 낸 문서는 남습니다.', attention: true },
  ],
};

const excelRunsHints: ScreenHintSet = {
  docId: 'programs-excel',
  screens: ['/programs/[id]/manage/excel/[formId]'],
  hints: [
    { id: 'excel-run-start', title: '차수 엑셀·PDF', short: '「k차 엑셀」은 1~k차를 지금 값으로 그리고, 「k차 PDF」는 k차 제출본만 묶습니다.', detail: '「다음」에서 받으면 고른 건에 새 차수가 적힙니다. 결재가 끝나면 그 차수 화면에서 승인할 건을 체크하고 「승인 메일 발송」을 누릅니다. 체크를 뺀 건은 결재 대기로 남습니다.' },
    { id: 'excel-run-notes', title: '확인할 서류', short: '가장 최근에 받은 엑셀에서 넣지 못한 서류나 금액을 바꿔 넣은 서류를 이름·서류·확인할 것으로 적습니다.', detail: '「엑셀 명단의 학번 칸에 … 없어」는 빈 엑셀 서식의 명단에 그 학생(팀) 줄이 없다는 뜻입니다. 받은 엑셀에 직접 적거나, 서류의 값이 틀렸으면 고친 뒤 다시 받습니다.' },
  ],
};

const excelGongmunHints: ScreenHintSet = {
  docId: 'programs-excel',
  screens: ['/programs/[id]/manage/excel/[formId]/gongmun'],
  hints: [
    { id: 'excel-gongmun', title: '지출 공문', short: '「공문 만들기」로 k차 공문을 만들고 「본문 복사」로 기안기에 붙여 넣습니다.', detail: '처음 한 번 지난 공문 HWP를 견본으로 올립니다. 「관련 공문」 번호는 엑셀마다 기억합니다. 자료에 없는 값은 빈칸으로 두고 아래에 적습니다.' },
  ],
};

const excelTrainingHints: ScreenHintSet = {
  docId: 'programs-excel',
  screens: ['/programs/[id]/manage/excel/[formId]/training'],
  hints: [
    { id: 'excel-training-actions', title: '결재 준비 설정', short: '예시를 올린 뒤 「AI로 설정하기」를 누르세요.', detail: '「확인할 칸」에서 틀린 칸을 살펴봅니다. 채우는 방법 전체를 바꾸려면 「처음부터 설정」을 누릅니다.' },
  ],
};

const templateWorkbenchHints: ScreenHintSet = {
  docId: 'templates',
  screens: ['/templates/[id]'],
  hints: [
    { id: 'template-workbench-faces', title: '본문과 슬롯', short: '본문을 보고 슬롯·가이드를 확인하세요.', detail: '가이드는 서식에 인쇄된 예시 문구로, 화면에서만 보이고 제출본에는 남지 않습니다. 「예시 작성본」은 점검에서 AI가 넣은 예시 값을 보여 줍니다.' },
    { id: 'template-workbench-save', title: '서식 저장', short: '변경한 서식은 저장을 눌러 반영하세요.', detail: '저장은 이름·종류·작성 안내 글·사진 선택을 반영합니다. 본문은 「버전」 탭 「본문 고치기」에서 새 버전으로 고칩니다.' },
  ],
};

const templateEditHints: ScreenHintSet = {
  docId: 'templates',
  screens: ['/templates/[id]/edit'],
  hints: [
    { id: 'template-edit-save', title: '본문 고치기', short: '고친 본문은 새 버전으로 저장됩니다.', detail: '저장해도 지금 쓰는 버전은 그대로입니다. 작업대 「버전」 탭에서 「이 버전으로 쓰기」를 눌러야 새 참여자에게 나갑니다.' },
  ],
};

const templateNewHints: ScreenHintSet = {
  docId: 'templates',
  screens: ['/templates/new'],
  hints: [
    { id: 'template-new-workspace', title: '서식 등록', short: 'HWP·HWPX 파일을 올리고 내용을 확인하세요.', detail: '등록 전에 서식 이름과 종류를 확인하고 저장하세요.' },
  ],
};

const templateSplitHints: ScreenHintSet = {
  docId: 'templates',
  screens: ['/templates/split'],
  hints: [
    { id: 'template-split-upload', title: '묶음 서식 나누기', short: '묶음 파일을 올리고 자를 자리를 확인하세요.', detail: '나뉜 결과를 확인한 뒤 필요한 서식을 등록하세요.' },
  ],
};

export const SCREEN_HINT_SETS: ScreenHintSet[] = [
  recruitHints, dashboardHints, documentsHints, programsHints, templatesHints,
  prefillHints, timetableHints, settingsHints, settingsAboutHints, settingsLicensesHints, adminUsersHints, adminIssuesHints, adminSystemHints, adminUsageHints,
  adminConsentListHints, adminConsentDetailHints, adminConsentEditorHints,
  programDetailHints, submissionHints, manageHints, budgetHints, intakeHints, excelHints, teamsHints,
  myProgramHints, myProgramBudgetHints, myIssuesHints,
  startHints, consentHints, guestApplyHints, guestUploadHints, editorHints,
  fillDocListHints, submissionDetailHints, recruitDetailHints, intakeDetailHints, excelRunsHints, excelGongmunHints, excelTrainingHints,
  templateWorkbenchHints, templateEditHints, templateNewHints, templateSplitHints,
];

function matchesScreen(pattern: string, pathname: string): boolean {
  const expected = pattern.split('/').filter(Boolean);
  const actual = pathname.split('/').filter(Boolean);
  return expected.length === actual.length && expected.every((segment, i) =>
    /^\[.+\]$/.test(segment) || segment === actual[i]);
}

export function getScreenHints(pathname: string | null): ScreenHintSet | null {
  if (!pathname) return null;
  let best: { set: ScreenHintSet; specificity: number } | null = null;
  for (const set of SCREEN_HINT_SETS) {
    for (const screen of set.screens) {
      if (!matchesScreen(screen, pathname)) continue;
      const specificity = screen.split('/').filter((segment) => segment && !/^\[.+\]$/.test(segment)).length;
      if (!best || specificity > best.specificity) best = { set, specificity };
    }
  }
  return best?.set ?? null;
}
